import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Pencil, Save, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, resolveImg } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const empty = {
  id: "",
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  published: true,
};

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? "" : d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
};

export default function ArticlesPanel() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/articles");
      setArticles(data);
    } catch {
      toast.error("Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...empty, id: crypto.randomUUID(), _new: true });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing({ ...a });
    setOpen(true);
  };

  const save = async () => {
    if (!editing.title) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload._new;
      if (editing._new) {
        await api.post("/admin/articles", payload);
      } else {
        await api.put(`/admin/articles/${editing.id}`, payload);
      }
      toast.success("Articol salvat");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/articles/${id}`);
    setArticles((a) => a.filter((x) => x.id !== id));
    toast.success("Articol șters");
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">{articles.length} articole</p>
        <Button
          onClick={openNew}
          data-testid="admin-article-add-button"
          className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" /> Adaugă articol
        </Button>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Newspaper className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Niciun articol încă. Apasă „Adaugă articol".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.id}`} className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex gap-4">
              <div className="w-20 h-20 rounded-lg bg-[#F4F6F9] overflow-hidden shrink-0 flex items-center justify-center">
                {a.cover_image ? (
                  <img src={resolveImg(a.cover_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Newspaper className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-[#0F1E36] truncate">{a.title}</h3>
                  {!a.published && (
                    <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">ciornă</span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 font-mono">/articole/{a.slug}</p>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{a.excerpt}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`admin-article-edit-${a.id}`} className="border-[#E2E8F0] h-8">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editează
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`admin-article-delete-${a.id}`} className="border-[#FEE2E2] text-[#DC2626] hover:bg-[#FEE2E2] h-8">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Ștergi articolul „{a.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>Articolul va fi eliminat definitiv de pe site.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Anulează</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a.id)} className="bg-[#DC2626] hover:bg-[#B91C1C]">Șterge</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?._new ? "Articol nou" : "Editează articol"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Titlu *</Label>
                <Input
                  data-testid="admin-article-title-input"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="mt-1.5"
                  placeholder="ex: Când trebuie să faci ITP-ul?"
                />
              </div>
              <div>
                <Label className="font-semibold">Rezumat scurt (apare în listă și la partajare)</Label>
                <Textarea
                  data-testid="admin-article-excerpt-input"
                  value={editing.excerpt}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  rows={2}
                  className="mt-1.5"
                />
              </div>
              <ImageUpload
                label="Imagine copertă"
                value={editing.cover_image}
                onChange={(url) => setEditing({ ...editing, cover_image: url })}
              />
              <div>
                <Label className="font-semibold">Conținut</Label>
                <div className="mt-1.5">
                  <RichTextEditor
                    key={editing.id}
                    value={editing.content}
                    onChange={(html) => setEditing({ ...editing, content: html })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between bg-[#F4F6F9] rounded-lg px-4 py-3">
                <Label className="font-semibold">Publicat pe site</Label>
                <Switch
                  data-testid="admin-article-published-switch"
                  checked={editing.published}
                  onCheckedChange={(v) => setEditing({ ...editing, published: v })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E2E8F0]">Anulează</Button>
            <Button onClick={save} disabled={saving} data-testid="admin-article-save-button" className="bg-[#FF5A1F] hover:bg-[#E04B14] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Salvează</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
